import type { CanvasSource, Map as MapLibreMap } from 'maplibre-gl';

import {
  OBSCURATION_BOUNDS,
  OBSCURATION_CACHE_SIZE,
  OBSCURATION_HEIGHT,
  OBSCURATION_STEP_MS,
  OBSCURATION_WIDTH,
} from './config';
import type { ObscurationWorkerRequest, ObscurationWorkerResponse } from './types';

const SOURCE_ID = 'eclipse-obscuration';
const LAYER_ID = 'eclipse-obscuration-surface';
const REQUEST_DEBOUNCE_MS = 70;

interface CachedFrame {
  coverage: Uint8Array;
  central: Uint8Array;
}

export interface ObscurationOverlay {
  setTime: (date: Date) => void;
  setVisible: (visible: boolean) => void;
  destroy: () => void;
}

export function obscurationAlpha(coverageByte: number, central: boolean): number {
  if (central) return 224;
  const coverage = coverageByte / 255;
  if (coverage <= 0.25) return 0;
  const normalized = (coverage - 0.25) / 0.75;
  // Keep the partial phase legible without letting near-total partial
  // obscuration visually merge with the instantaneous umbral footprint.
  return Math.round(108 * normalized ** 2.4);
}

function colorizeFrame(frame: CachedFrame, pixels: Uint8ClampedArray): void {
  for (let index = 0; index < frame.coverage.length; index += 1) {
    const centralStrength = (frame.central[index] ?? 0) / 255;
    const baseAlpha = obscurationAlpha(frame.coverage[index] ?? 0, false);
    const pixelIndex = index * 4;
    pixels[pixelIndex] = Math.round(7 - centralStrength * 6);
    pixels[pixelIndex + 1] = Math.round(8 - centralStrength * 7);
    pixels[pixelIndex + 2] = Math.round(15 - centralStrength * 13);
    pixels[pixelIndex + 3] = Math.round(baseAlpha + centralStrength * (224 - baseAlpha));
  }
}

export function createObscurationOverlay(map: MapLibreMap): ObscurationOverlay {
  const canvas = document.createElement('canvas');
  canvas.width = OBSCURATION_WIDTH;
  canvas.height = OBSCURATION_HEIGHT;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Canvas is unavailable');
  const renderedPixels = new Uint8ClampedArray(OBSCURATION_WIDTH * OBSCURATION_HEIGHT * 4);
  const renderedImage = new ImageData(renderedPixels, OBSCURATION_WIDTH, OBSCURATION_HEIGHT);

  map.addSource(SOURCE_ID, {
    type: 'canvas',
    canvas,
    animate: false,
    coordinates: [
      [OBSCURATION_BOUNDS.west, OBSCURATION_BOUNDS.north],
      [OBSCURATION_BOUNDS.east, OBSCURATION_BOUNDS.north],
      [OBSCURATION_BOUNDS.east, OBSCURATION_BOUNDS.south],
      [OBSCURATION_BOUNDS.west, OBSCURATION_BOUNDS.south],
    ],
  });
  map.addLayer({
    id: LAYER_ID,
    type: 'raster',
    source: SOURCE_ID,
    paint: {
      'raster-opacity': 1,
      'raster-fade-duration': 0,
      'raster-resampling': 'linear',
    },
  });
  map.moveLayer(LAYER_ID, 'totality-fill');

  const worker = new Worker(new URL('./obscuration.worker.ts', import.meta.url), {
    type: 'module',
  });
  const cache = new Map<number, CachedFrame>();
  let visible = true;
  let destroyed = false;
  let debounceTimer = 0;
  let requestId = 0;
  let activeRequestId: number | null = null;
  let pendingTimeMs: number | null = null;
  let currentTimeMs: number | null = null;

  const draw = (frame: CachedFrame) => {
    if (destroyed) return;
    colorizeFrame(frame, renderedPixels);
    context.putImageData(renderedImage, 0, 0);
    const source = map.getSource<CanvasSource>(SOURCE_ID);
    if (!source) return;
    source.play();
    map.triggerRepaint();
    requestAnimationFrame(() => {
      if (!destroyed) source.pause();
    });
  };

  const remember = (timeMs: number, frame: CachedFrame) => {
    cache.delete(timeMs);
    cache.set(timeMs, frame);
    const oldest = cache.keys().next().value;
    if (cache.size > OBSCURATION_CACHE_SIZE && oldest !== undefined) cache.delete(oldest);
  };

  const requestFrame = (timeMs: number) => {
    const cached = cache.get(timeMs);
    if (cached) {
      pendingTimeMs = null;
      remember(timeMs, cached);
      draw(cached);
      return;
    }
    if (activeRequestId !== null) {
      pendingTimeMs = timeMs;
      return;
    }
    activeRequestId = ++requestId;
    const request: ObscurationWorkerRequest = {
      type: 'render',
      requestId: activeRequestId,
      timeMs,
    };
    worker.postMessage(request);
  };

  worker.onmessage = (event: MessageEvent<ObscurationWorkerResponse>) => {
    if (event.data.type !== 'frame' || destroyed) return;
    const frame = {
      coverage: new Uint8Array(event.data.coverageBuffer),
      central: new Uint8Array(event.data.centralBuffer),
    };
    remember(event.data.timeMs, frame);
    if (event.data.requestId === activeRequestId) activeRequestId = null;
    if (event.data.timeMs === currentTimeMs && visible) {
      draw(frame);
    }
    const pending = pendingTimeMs;
    pendingTimeMs = null;
    if (pending !== null && pending !== event.data.timeMs && visible) requestFrame(pending);
  };

  worker.onerror = () => {
    if (map.getLayer(LAYER_ID)) map.setLayoutProperty(LAYER_ID, 'visibility', 'none');
  };

  return {
    setTime(date) {
      currentTimeMs = Math.round(date.getTime() / OBSCURATION_STEP_MS) * OBSCURATION_STEP_MS;
      if (!visible) return;
      window.clearTimeout(debounceTimer);
      debounceTimer = window.setTimeout(() => {
        if (currentTimeMs !== null) requestFrame(currentTimeMs);
      }, REQUEST_DEBOUNCE_MS);
    },
    setVisible(nextVisible) {
      if (visible === nextVisible) return;
      visible = nextVisible;
      if (map.getLayer(LAYER_ID)) {
        map.setLayoutProperty(LAYER_ID, 'visibility', visible ? 'visible' : 'none');
      }
      if (visible && currentTimeMs !== null) requestFrame(currentTimeMs);
    },
    destroy() {
      destroyed = true;
      window.clearTimeout(debounceTimer);
      worker.terminate();
      if (map.getLayer(LAYER_ID)) map.removeLayer(LAYER_ID);
      if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID);
    },
  };
}
